import { InputBaseComponentProps, SxProps } from '@mui/material';
import { ChangeEvent, forwardRef } from 'react';
import { NumericFormat, NumericFormatProps } from 'react-number-format';

import TextField, { MUITextFieldProps } from './TextField';

const NumberFormat = forwardRef((props: NumericFormatProps, ref) => {
    const { onChange, ...inputProps } = props;

    return (
        <NumericFormat
            getInputRef={ref}
            onValueChange={(values) => {
                // NumericFormat reports a value, not an event, so the change is synthesised.
                // Consumers only ever read `target.name` and `target.value`, which is why the
                // two-property stand-in works — the cast is what says so out loud.
                onChange?.({
                    target: {
                        name: props.name as string,
                        value: values.floatValue?.toString() || '0',
                    },
                } as ChangeEvent<HTMLInputElement>);
            }}
            {...inputProps}
        />
    );
});

/**
 * Formik Wrapper for numeric inputs
 *
 * @example <AmountField label="Test" name="test" inputComponentProps={{ prefix: 'ft' }} />
 *
 * @param {Object} props
 * @param {string} props.name - unique field name in your form
 */
function AmountField({
    InputProps = {},
    inputComponentProps = {},
    sx = {},
    isShowError,
    error,
    ...props
}: Omit<MUITextFieldProps, 'error'> & {
    name: string;
    inputComponentProps?: Record<string, unknown>;
    isShowError?: boolean;
    /**
     * The message to show, not a flag — it lands in `helperText` and also turns the error state on.
     * `MUITextFieldProps['error']` is omitted above because a boolean intersected with this string
     * is `never`, which left the prop accepting nothing but `undefined`.
     */
    error?: string;
    sx?: SxProps;
}) {
    return (
        <TextField
            // TODO: update InputProps (deprecated) to slotProps
            InputProps={{
                inputComponent: NumberFormat as React.ElementType<InputBaseComponentProps>,
                inputProps: {
                    prefix: '',
                    thousandSeparator: true,
                    ...inputComponentProps,
                },
                ...InputProps,
            }}
            {...props}
            sx={sx}
            error={isShowError || !!error}
            helperText={error}
        />
    );
}

export default AmountField;
